<template>
  <div>
    <v-row dense class="mx-0">
      <v-col v-for="card in cards" :key="card.key" cols="6" sm="4" md="2">
        <v-alert :color="card.color" dense>
          <div class="text-caption text--secondary">{{ card.label }}</div>
          <div class="text-h6">{{ card.value }}</div>
        </v-alert>
      </v-col>
    </v-row>

    <h6 class="mt-2 mb-2">Distribuição</h6>
    <div class="status-bar" role="img" aria-label="Percentual de cada status">
      <div v-if="!total" class="status-bar__empty">Sem destinatários</div>
      <button
        v-for="segment in visibleSegments"
        :key="segment.key"
        type="button"
        class="status-bar__seg"
        :class="{ 'status-bar__seg--active': status === segment.key }"
        :style="{ width: segment.percent + '%', background: segment.color }"
        :title="segment.label + ' · ' + segment.percentLabel"
        @click="setStatus(segment.key)"
      >
        <span v-if="segment.percent >= 8">{{ segment.percentLabel }}</span>
      </button>
    </div>

    <div class="d-flex flex-wrap mt-3">
      <v-chip
        v-for="segment in segments"
        :key="segment.key"
        small
        class="mr-2 mb-2"
        :outlined="status !== segment.key"
        :color="status === segment.key ? segment.color : undefined"
        :text-color="status === segment.key ? 'white' : undefined"
        @click="setStatus(segment.key)"
      >
        <span v-if="segment.key !== status" class="status-dot mr-2" :style="{ background: segment.color }" />
        {{ segment.label }}
      </v-chip>
    </div>
    <div class="text-caption text--secondary mb-4">
      Cada pessoa entra em um status. Quem leu deixa de contar como enviado.
      Clique em uma faixa para filtrar a lista.
    </div>

    <v-card class="rounded-lg pa-3 pb-1" outlined>
      <div class="d-flex mb-2">
        <h6 class="mb-0">Envios</h6>
        <v-spacer />
        <v-text-field
          v-model="query"
          :label="
            channel === 'EMAIL'
              ? 'Buscar por e-mail ou nome'
              : 'Buscar por número ou nome'
          "
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="search-field"
          @input="scheduleSearch"
        />
      </div>

      <v-alert v-if="error" type="error" text dense class="mb-2">{{
        error
      }}</v-alert>

      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="listTotal"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        item-key="id"
        dense
        no-data-text="Nenhum envio encontrado"
      >
        <template v-slot:item.name="{ item }">
          {{ item.name || "—" }}
        </template>
        <template v-slot:item.contact="{ item }">
          <span class="text-no-wrap">{{ item.contact || "—" }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small label :color="colorOf(item.status)" dark>
            {{ labelOf(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.detail="{ item }">
          {{ item.detail || "—" }}
        </template>
        <template v-slot:item.at="{ item }">
          {{ formatDate(item.at) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
const COLORS = {
  SKIPPED: "#9e9e9e",
  PENDING: "#fb8c00",
  SENT: "#1e88e5",
  READ: "#43a047",
  FAILED: "#e53935",
};

const LABELS = {
  SKIPPED: "Ignorados",
  PENDING: "Pendentes",
  SENT: "Enviados",
  READ: "Lidos",
  FAILED: "Falhas",
};

export default {
  props: {
    counts: { type: Object, default: () => ({}) },
    channel: { type: String, default: "WHATSAPP" },
    campaignId: { type: String, default: "" },
    api: { type: Object, default: null },
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    status: null,
    query: "",
    queryTimer: null,
    items: [],
    listTotal: 0,
    loading: false,
    error: "",
    options: { page: 1, itemsPerPage: 25 },
  }),
  computed: {
    total() {
      return Number(this.counts?.total) || 0;
    },
    readCount() {
      return this.channel === "WHATSAPP" ? Number(this.counts?.read) || 0 : 0;
    },
    buckets() {
      return {
        SKIPPED: Number(this.counts?.skipped) || 0,
        PENDING: Number(this.counts?.pending) || 0,
        SENT: Math.max((Number(this.counts?.sent) || 0) - this.readCount, 0),
        READ: this.readCount,
        FAILED: Number(this.counts?.failed) || 0,
      };
    },
    segments() {
      const keys = ["SKIPPED", "PENDING", "SENT", "FAILED"];
      if (this.channel === "WHATSAPP") keys.splice(3, 0, "READ");
      return keys.map((key) => {
        const value = this.buckets[key];
        const percent = this.total ? (value / this.total) * 100 : 0;
        return {
          key,
          label: LABELS[key],
          value,
          color: COLORS[key],
          percent,
          percentLabel: this.percentLabel(percent),
        };
      });
    },
    visibleSegments() {
      return this.segments.filter((segment) => segment.value > 0);
    },
    cards() {
      return [{ key: "TOTAL", label: "Total", value: this.total }].concat(
        this.segments.map((segment) => ({
          ...segment,
          key: segment.key,
          label: segment.label,
          value: segment.value,
        }))
      );
    },
    headers() {
      return [
        { text: "Nome", value: "name", sortable: false },
        {
          text: this.channel === "EMAIL" ? "E-mail" : "Número",
          value: "contact",
          sortable: false,
        },
        { text: "Status", value: "status", sortable: false },
        { text: "Detalhe", value: "detail", sortable: false },
        { text: "Quando", value: "at", sortable: false },
      ];
    },
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
      immediate: true,
    },
    refreshKey() {
      this.load({ quiet: this.items.length > 0 });
    },
    campaignId() {
      this.status = null;
      this.query = "";
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
  },
  beforeDestroy() {
    clearTimeout(this.queryTimer);
  },
  methods: {
    percentLabel(percent) {
      if (percent > 0 && percent < 1) return "<1%";
      return `${Math.round(percent)}%`;
    },
    colorOf(status) {
      return COLORS[status] || "#9e9e9e";
    },
    labelOf(status) {
      const labels = {
        SKIPPED: "Ignorado",
        PENDING: "Pendente",
        SENT: "Enviado",
        READ: "Lido",
        FAILED: "Falha",
      };
      return labels[status] || status;
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("pt-BR");
    },
    setStatus(key) {
      this.status = this.status === key ? null : key;
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
    scheduleSearch() {
      clearTimeout(this.queryTimer);
      this.queryTimer = setTimeout(() => {
        if (this.options.page !== 1)
          this.options = { ...this.options, page: 1 };
        else this.load();
      }, 400);
    },
    async load({ quiet } = {}) {
      if (!this.campaignId || !this.api) return;
      if (!quiet) this.loading = true;
      this.error = "";
      try {
        const { page, itemsPerPage } = this.options;
        const params = {
          take: itemsPerPage,
          skip: (page - 1) * itemsPerPage,
        };
        if (this.status) params.status = this.status;
        if (this.query && this.query.trim()) params.q = this.query.trim();
        const data = await this.api.recipients(this.campaignId, params);
        this.items = data.items || [];
        this.listTotal = Number(data.total) || 0;
      } catch (error) {
        this.error = error.message || "Não foi possível carregar os envios";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.status-bar {
  display: flex;
  height: 28px;
  border-radius: 6px;
  overflow: hidden;
  background: #eeeeee;
}
.status-bar__empty {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #757575;
}
.status-bar__seg {
  border: 0;
  padding: 0;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  min-width: 0;
}
.status-bar__seg--active {
  outline: 2px solid #212121;
  outline-offset: -2px;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.search-field {
  max-width: 320px;
}
</style>
